import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import StaffNavItem from './StaffNavItem';
import Icon from './Icon';
import { isMerchantSpecialistUser } from '../utils/portalRouting';
import homecrowdIcon from '../assets/images/homecrowd-icon.png';
import '../styles/StaffNavbar.css';

// .admin-main is the scroller, and each page mounts its own, so the browser
// cannot put you back where you left. Remember the position per path.
const adminMainScroll = new Map();

function useRestoreAdminMainScroll() {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    let main = null;
    let detach = () => {};

    const bind = () => {
      const next = document.querySelector('.admin-main');
      if (next === main) return;
      detach();
      main = next;
      if (!main) return;

      const apply = () => {
        const saved = adminMainScroll.get(pathname);
        // The loading shell is shorter than the saved offset, so the first
        // assignment clamps to 0. Re-apply once the real page has height.
        if (typeof saved === 'number' && Math.abs(main.scrollTop - saved) > 1) {
          main.scrollTop = saved;
        }
      };
      const save = () => {
        if (main.scrollHeight <= main.clientHeight + 1) return;
        adminMainScroll.set(pathname, main.scrollTop);
      };

      apply();
      main.addEventListener('scroll', save, { passive: true });
      // The scroller's own box does not change when the page replaces
      // "Loading..." with the real content. Watch the content height.
      const resize = new ResizeObserver(apply);
      const content = main.querySelector('.admin-content');
      if (content) resize.observe(content);
      const inner = new MutationObserver(() => {
        const nextContent = main.querySelector('.admin-content');
        if (nextContent) resize.observe(nextContent);
        apply();
      });
      inner.observe(main, { childList: true, subtree: true });
      detach = () => {
        save();
        resize.disconnect();
        inner.disconnect();
        main.removeEventListener('scroll', save);
      };
    };

    bind();
    const parent = document.querySelector('.admin-dashboard') || document.body;
    const observer = new MutationObserver(bind);
    observer.observe(parent, { childList: true });
    return () => {
      observer.disconnect();
      detach();
    };
  }, [pathname]);
}

const schoolLogoImgStyle = {
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  borderRadius: '4px'
};

const HeaderLogo = ({ loading, imageSrc, alt = '' }) => (
  <div
    className="school-logo"
    style={{
      width: '50px',
      height: '50px',
      borderRadius: '4px',
      backgroundColor: loading ? '#f0f0f0' : 'transparent',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}
  >
    {loading ? (
      <div
        style={{
          width: '24px',
          height: '24px',
          border: '2px solid #ddd',
          borderTop: '2px solid #666',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}
      />
    ) : (
      <img
        src={imageSrc || homecrowdIcon}
        alt={alt}
        style={schoolLogoImgStyle}
      />
    )}
  </div>
);

const StaffNavbar = ({ activeSection = 'dashboard' }) => {
  const [user, setUser] = useState(null);
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [schoolPickerOpen, setSchoolPickerOpen] = useState(false);
  const [schools, setSchools] = useState([]);
  const [schoolsLoading, setSchoolsLoading] = useState(false);
  const [switchingSchool, setSwitchingSchool] = useState(false);
  const schoolPickerRef = useRef(null);
  const navigate = useNavigate();
  useRestoreAdminMainScroll();

  const closeMobile = () => setIsOpen(false);
  const go = (path) => { closeMobile(); navigate(path); };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const currentUser = await apiService.fetchCurrentUser();
        setUser(currentUser);

        if (!currentUser?.is_staff && !isMerchantSpecialistUser(currentUser)) {
          navigate('/login/');
          return;
        }

        if (currentUser?.active_school && !isMerchantSpecialistUser(currentUser)) {
          setSchool(currentUser.active_school);
        }
      } catch (error) {
        console.error('Failed to fetch user:', error);
        navigate('/login/');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [navigate]);

  useEffect(() => {
    if (!schoolPickerOpen) return undefined;
    const onPointerDown = (event) => {
      if (schoolPickerRef.current && !schoolPickerRef.current.contains(event.target)) {
        setSchoolPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [schoolPickerOpen]);

  const handleLogout = () => {
    // Clear authentication data
    apiService.logout();

    // Force redirect to login page
    window.location.href = '/login/';
  };

  const openSchoolPicker = async () => {
    if (!user?.is_superuser || switchingSchool) return;
    const nextOpen = !schoolPickerOpen;
    setSchoolPickerOpen(nextOpen);
    if (!nextOpen || schools.length > 0) return;
    setSchoolsLoading(true);
    try {
      const data = await apiService.getSchools();
      const list = Array.isArray(data) ? data : (data?.results || []);
      setSchools(
        list
          .filter((item) => item?.is_active !== false)
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      );
    } catch (error) {
      console.error('Failed to load schools:', error);
    } finally {
      setSchoolsLoading(false);
    }
  };

  const selectSchool = async (nextSchool) => {
    if (!nextSchool?.id || switchingSchool) return;
    if (String(nextSchool.id) === String(school?.id)) {
      setSchoolPickerOpen(false);
      return;
    }
    setSwitchingSchool(true);
    try {
      const response = await apiService.switchActiveSchoolSuperuser(nextSchool.id);
      if (response?.user) {
        localStorage.setItem('user', JSON.stringify(response.user));
      }
      window.location.reload();
    } catch (error) {
      console.error('Failed to switch school:', error);
      setSwitchingSchool(false);
    }
  };

  const isSpecialist = isMerchantSpecialistUser(user);
  const sidebarItems = isSpecialist
    ? [
        {
          id: 'merchant-pay',
          label: 'Merchant Pay',
          icon: 'card',
          path: '/superuser/invoices/'
        }
      ]
    : [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: 'grid',
      path: '/staff/dashboard/'
    },
    {
      id: 'fans',
      label: 'Fans',
      icon: 'people',
      path: '/staff/fans/'
    },
    {
      id: 'transactions',
      label: 'Transactions',
      icon: 'card',
      path: '/staff/transactions/'
    },
    // Receipt review now lives at the superuser level only — see the
    // "Review Receipts" tile on /superuser/dashboard/, which points at
    // /superuser/receipts/. The staff-scoped route still exists.
    // {
    //   id: 'receipt-reviews',
    //   label: 'Review Receipts',
    //   icon: 'card',
    //   path: '/staff/receipts/',
    //   isSubcategory: true
    // },
    // {
    //   id: 'content',
    //   label: 'Content',
    //   icon: 'play',
    //   path: '/staff/content/'
    // },
    {
      id: 'rewards',
      label: 'Rewards',
      icon: 'gift',
      path: '/staff/rewards/'
    },
    {
      id: 'raffles',
      label: 'Raffles',
      icon: 'ticket',
      path: '/staff/raffles/',
      isSubcategory: true
    },
    // {
    //   id: 'auctions',
    //   label: 'Auctions',
    //   icon: 'hammer',
    //   path: '/staff/auctions/',
    //   isSubcategory: true
    // },
    {
      id: 'weekly-rewards',
      label: 'Weekly Rewards',
      icon: 'gift',
      path: '/staff/weekly-rewards/',
      isSubcategory: true
    },
    {
      id: 'overall-rewards',
      label: 'Season Rewards',
      icon: 'gift',
      path: '/staff/overall-rewards/',
      isSubcategory: true
    },
    {
      id: 'initial-rewards',
      label: 'Initial Rewards',
      icon: 'gift',
      path: '/staff/initial-rewards/',
      isSubcategory: true
    },
    {
      id: 'fulfillment',
      label: 'Reward Delivery',
      icon: 'gift',
      path: '/staff/fulfillment/',
      isSubcategory: true
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: 'activity',
      path: '/staff/analytics/'
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: 'settings',
      path: '/staff/settings/'
    }
  ];

  if (!user) {
    return (
      <div className="admin-sidebar">
        <div className="navbar-loading">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        className="admin-sidebar-toggle"
        aria-label={isOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={isOpen}
        onClick={() => setIsOpen(v => !v)}
      >
        <span />
        <span />
        <span />
      </button>
      {isOpen && <div className="admin-sidebar-backdrop" onClick={closeMobile} />}
      <div className={`admin-sidebar${isOpen ? ' open' : ''}`}>
        <div className="admin-header">
          <div className={`admin-logo${user.is_superuser ? ' school-switcher' : ''}`} ref={schoolPickerRef}>
            {user.is_superuser ? (
              <button
                type="button"
                className="school-switcher-trigger"
                onClick={openSchoolPicker}
                disabled={switchingSchool}
                aria-haspopup="listbox"
                aria-expanded={schoolPickerOpen}
              >
                <HeaderLogo loading={loading} imageSrc={school?.image} />
                <div className="logo-text">
                  <div className="school-name">
                    {loading ? (
                      <div
                        style={{
                          width: '120px',
                          height: '16px',
                          backgroundColor: '#f0f0f0',
                          borderRadius: '8px'
                        }}
                      />
                    ) : (
                      school?.name || 'HomeCrowd'
                    )}
                  </div>
                  <div className="portal-text">Admin Portal</div>
                </div>
                <span className={`school-switcher-caret${schoolPickerOpen ? ' open' : ''}`} aria-hidden="true" />
              </button>
            ) : (
              <>
            <HeaderLogo loading={loading} imageSrc={!isSpecialist ? school?.image : null} alt="School Logo" />
            <div className="logo-text">
              <div className="school-name">
                {loading ? (
                  <div
                    style={{
                      width: '120px',
                      height: '16px',
                      backgroundColor: '#f0f0f0',
                      borderRadius: '8px'
                    }}
                  />
                ) : (
                  isSpecialist ? 'HomeCrowd' : (school?.name || 'School Name')
                )}
              </div>
              <div className="portal-text">{isSpecialist ? 'Merchant Pay' : 'Admin Portal'}</div>
            </div>
              </>
            )}
            {user.is_superuser && schoolPickerOpen && (
              <div className="school-switcher-menu" role="listbox">
                {schoolsLoading ? (
                  <div className="school-switcher-empty">Loading...</div>
                ) : schools.length === 0 ? (
                  <div className="school-switcher-empty">No active schools</div>
                ) : (
                  schools.map((item) => {
                    const selected = String(item.id) === String(school?.id);
                    return (
                      <button
                        key={item.id}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        className={`school-switcher-option${selected ? ' selected' : ''}`}
                        onClick={() => selectSchool(item)}
                        disabled={switchingSchool}
                      >
                        <span className="school-switcher-option-logo">
                          {item.image ? (
                            <img src={item.image} alt="" />
                          ) : (
                            <span className="school-switcher-option-logo-fallback" />
                          )}
                        </span>
                        <span className="school-switcher-option-name">{item.name}</span>
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>

        <nav className="admin-nav">
          {sidebarItems.map((item) => (
            <StaffNavItem
              key={item.id}
              icon={item.icon}
              label={item.label}
              isActive={activeSection === item.id}
              isSubcategory={item.isSubcategory}
              onClick={() => go(item.path)}
            />
          ))}
        </nav>

        <div className="admin-footer">
          <div className="user-info">
            <div className="user-details">
              <div className="user-name">{user.first_name} {user.last_name}</div>
              <div className="user-email">{user.email}</div>
            </div>
            {user.is_superuser && (
              <button
                className={`staff-nav-item ${activeSection === 'superuser' ? 'active' : ''}`}
                onClick={() => go('/superuser/dashboard/')}
                title="Superuser Dashboard"
                style={{
                  marginLeft: '8px',
                  minWidth: 'auto',
                  padding: '4px',
                  width: 'auto'
                }}
              >
                <span className="nav-icon">
                  <Icon name="grid" size={16} />
                </span>
              </button>
            )}
          </div>

          <StaffNavItem
            icon="leave"
            label="Sign Out"
            isActive={false}
            onClick={handleLogout}
          />
        </div>
      </div>
    </>
  );
};

export default StaffNavbar;